import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from '../components/shared/Modal';
import PricingModal from '../components/modals/PricingModal';
import TenantNameConflictModal from '../components/modals/TenantNameConflictModal';
import EmailVerificationBanner from '../components/notifications/EmailVerificationBanner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/customToast';
import { FiPlus, FiUsers, FiArrowRight, FiKey, FiArrowLeftCircle } from 'react-icons/fi';
import usePageTitle from '../hooks/usePageTitle';

const JoinTenant = () => {
    usePageTitle('Rejoindre une organisation');
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showJoinModal, setShowJoinModal] = useState(false);
    // theme detection: only apply CSS class (no local state required)
    const { getAuthHeaders, switchTenant, user, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const checkDarkMode = () => {
            const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            if (systemPrefersDark) {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }
        };
        checkDarkMode();
    }, []);

    const fetchTenants = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/my`, {
                headers: getAuthHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                setTenants(data);
            } else {
                console.error('Failed to fetch tenants');
                toast.error('Erreur lors du chargement des organisations');
            }
        } catch (error) {
            console.error('Error fetching tenants:', error);
            toast.error('Erreur lors du chargement des organisations');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchTenants();
    }, [fetchTenants]);

    const handleSelectTenant = async (tenantId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/switch/${tenantId}`, {
                method: 'PUT',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                localStorage.setItem('activeTenantId', tenantId);
                switchTenant(tenantId);
                toast.success('Organisation sélectionnée!');
                setTimeout(() => navigate('/messages'), 500);
            } else {
                throw new Error('Erreur lors de la sélection');
            }
        } catch (error) {
            console.error('Error selecting tenant:', error);
            toast.error('Erreur lors de la sélection de l\'organisation');
        }
    };

    const getPlanBadge = (plan) => {
        const colors = {
            free: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
            starter: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
            team: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
            business: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
            enterprise: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
        };
        return plan === 'free'
            ? `${colors.free} border border-red-400` // highlight free plan
            : colors[plan] || colors.free;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <>
            <EmailVerificationBanner />
            <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl p-8">
                    <h1 className="text-2xl font-bold text-center mb-2 text-black dark:text-white">
                        Bienvenue, {user?.name}
                    </h1>
                    <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
                        {tenants.length === 0
                            ? "Pour commencer, créez votre organisation ou rejoignez-en une existante"
                            : "Sélectionnez une organisation pour continuer"}
                    </p>

                    {tenants.length === 0 ? (
                        <div className="space-y-4">
                            <div className="text-center py-8">
                                <FiUsers className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Vous n'appartenez à aucune organisation
                                </p>
                            </div>

                            <button
                                onClick={() => {
                                    if (!user?.isEmailVerified) {
                                        toast.error('Veuillez d\'abord vérifier votre adresse email pour créer une organisation');
                                        return;
                                    }
                                    setShowCreateModal(true);
                                }}
                                disabled={!user?.isEmailVerified}
                                className={`w-full px-6 py-4 rounded-lg transition font-semibold flex items-center justify-center gap-3 ${user?.isEmailVerified
                                    ? 'bg-primary text-white dark:text-black hover:bg-primary-hover'
                                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                <FiPlus className="h-5 w-5" />
                                Créer une nouvelle organisation
                            </button>

                            <button
                                onClick={() => setShowJoinModal(true)}
                                className="w-full border-2 border-primary dark:border-gray-600 text-black dark:text-white px-6 py-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition font-semibold flex items-center justify-center gap-3"
                            >
                                <FiKey className="h-5 w-5" />
                                Rejoindre une organisation existante
                            </button>

                            <button
                                onClick={() => {
                                    logout();
                                    navigate('/login');
                                }}
                                className="w-full text-black dark:text-blue-400 hover:dark:text-blue-300 px-6 py-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition font-semibold flex items-center justify-center gap-3"
                            >
                                <FiArrowLeftCircle className="h-5 w-5" />
                                Retourner au login
                            </button>

                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Vous pouvez toujours créer ou rejoindre une organisation plus tard depuis les paramètres.
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {tenants.map((tenant) => (
                                <button
                                    key={tenant._id}
                                    onClick={() => handleSelectTenant(tenant._id)}
                                    className="w-full p-6 border-2 dark:border-gray-700 rounded-lg hover:border-primary dark:hover:border-primary transition text-left group"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-bold text-black dark:text-white">
                                                    {tenant.name}
                                                </h3>
                                                <span className={`text-xs px-2 py-1 rounded ${getPlanBadge(tenant.plan)}`}>
                                                    {tenant.plan}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                                Rôle: {tenant.userRole} • {tenant.type === 'personal' ? 'Personnel' : 'Professionnel'}
                                            </p>
                                            <div className="text-xs text-gray-500 dark:text-gray-500">
                                                {tenant.usage?.messages || 0}/{tenant.limits?.maxMessages || 100} messages •
                                                {tenant.usage?.todos || 0}/{tenant.limits?.maxTodos || 50} todos
                                            </div>
                                        </div>
                                        <FiArrowRight className="h-6 w-6 text-gray-400 group-hover:text-primary transition" />
                                    </div>
                                </button>
                            ))}

                            <div className="grid grid-cols-2 gap-4 pt-4">
                                <button
                                    onClick={() => {
                                        if (!user?.isEmailVerified) {
                                            toast.error('Veuillez d\'abord vérifier votre adresse email pour créer une organisation');
                                            return;
                                        }
                                        setShowCreateModal(true);
                                    }}
                                    disabled={!user?.isEmailVerified}
                                    className={`p-4 border-2 border-dashed rounded-lg transition flex flex-col items-center justify-center gap-2 ${user?.isEmailVerified
                                        ? 'dark:border-gray-700 hover:border-primary dark:hover:border-primary text-gray-600 dark:text-gray-400 hover:text-primary'
                                        : 'border-gray-300 dark:border-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                        }`}
                                >
                                    <FiPlus className="h-6 w-6" />
                                    <span className="font-semibold text-sm">Créer</span>
                                </button>

                                <button
                                    onClick={() => setShowJoinModal(true)}
                                    className="p-4 border-2 border-dashed dark:border-gray-700 rounded-lg hover:border-primary dark:hover:border-primary transition flex flex-col items-center justify-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary"
                                >
                                    <FiKey className="h-6 w-6" />
                                    <span className="font-semibold text-sm">Rejoindre</span>
                                </button>
                            </div>

                            <button
                                onClick={() => {
                                    logout();
                                    navigate('/login');
                                }}
                                className="w-full text-black dark:text-blue-400 hover:dark:text-blue-300 px-6 py-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition font-semibold flex items-center justify-center gap-3"
                            >
                                <FiArrowLeftCircle className="h-5 w-5" />
                                Retourner au login
                            </button>
                        </div>
                    )}
                </div>

                {showCreateModal && (
                    <CreateTenantModal
                        onClose={() => setShowCreateModal(false)}
                        onSuccess={() => {
                            setShowCreateModal(false);
                            fetchTenants();
                        }}
                    />
                )}

                {showJoinModal && (
                    <JoinTenantModal
                        onClose={() => setShowJoinModal(false)}
                        onSuccess={() => {
                            setShowJoinModal(false);
                            fetchTenants();
                        }}
                    />
                )}
            </div>
        </>
    );
};

const CreateTenantModal = ({ onClose, onSuccess }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState('professional');
    const [loading, setLoading] = useState(false);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictingName, setConflictingName] = useState('');
    const [selectedPlan, setSelectedPlan] = useState('free');
    const { getAuthHeaders, switchTenant } = useAuth();
    const navigate = useNavigate();
    const nameInputRef = useRef(null);

    // Open plan selection modal instead of immediate creation
    const handleOpenPlan = (e) => {
        e.preventDefault();
        setShowPlanModal(true);
    };

    const createTenant = async (plan = 'free') => {
        setLoading(true);
        try {
            // Force creation with 'free' plan only - paid plans require Stripe upgrade
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ name, type, plan: 'free' })
            });

            if (response.ok) {
                const data = await response.json();
                setShowPlanModal(false);

                // Switch to new tenant first
                try {
                    await switchTenant(data._id);
                } catch (err) {
                    localStorage.setItem('activeTenantId', data._id);
                    window.location.reload();
                }

                // Seul le plan gratuit peut être créé maintenant
                toast.success('Organisation créée avec succès! Vous pouvez upgrader vers un plan payant depuis les paramètres.');

                onSuccess();
                navigate('/messages');
            } else {
                const data = await response.json().catch(() => ({ message: 'Erreur serveur' }));

                // Vérifier si c'est une erreur de nom existant
                const isNameConflict = response.status === 400 &&
                    data.message &&
                    (data.message.includes('existe déjà') ||
                        data.message.includes('existe deja') ||
                        data.message.toLowerCase().includes('organisation existe'));

                if (isNameConflict) {
                    setConflictingName(name);
                    setShowPlanModal(false);
                    // Petit délai pour s'assurer que la modale de plan est fermée
                    setTimeout(() => setShowConflictModal(true), 150);
                } else {
                    toast.error(data.message || 'Erreur lors de la création');
                }
            }
        } catch (error) {
            console.error('Error creating tenant:', error);
            toast.error('Erreur réseau');
        } finally {
            setLoading(false);
        }
    };

    const createModalActions = (
        <>
            <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
            >
                Annuler
            </button>
            <button
                type="submit"
                form="create-tenant-form"
                disabled={loading || name.trim() === '' || name.trim().length > 40}
                className="flex-1 bg-primary text-white dark:text-black font-bold py-2 px-4 rounded hover:bg-primary-hover disabled:bg-gray-400 dark:disabled:bg-gray-600"
            >
                {loading ? 'Création...' : 'Suivant : choisir un plan'}
            </button>
        </>
    );

    return (
        <>
            <Modal
                open={!showPlanModal && !showConflictModal}
                onClose={onClose}
                title="Créer une organisation"
                actions={createModalActions}
                initialFocusRef={nameInputRef}
            >
                <form id="create-tenant-form" onSubmit={handleOpenPlan} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nom de l'organisation
                        </label>
                        <input
                            ref={nameInputRef}
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                            required
                            disabled={loading}
                            placeholder="Mon entreprise"
                            maxLength={40}
                        />
                        <div className="text-xs text-gray-500 pt-1">{name.trim().length}/40</div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Type
                        </label>
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                            disabled={loading}
                        >
                            <option value="professional">Professionnel</option>
                            <option value="personal">Personnel</option>
                        </select>
                    </div>
                </form>
            </Modal>

            <PricingModal
                open={showPlanModal}
                onClose={() => setShowPlanModal(false)}
                currentPlan={selectedPlan}
                tenantId={null}
                onSelect={(planId) => {
                    // create tenant with selected plan
                    setSelectedPlan(planId);
                    createTenant(planId);
                }}
            />

            <TenantNameConflictModal
                open={showConflictModal}
                onClose={() => {
                    setShowConflictModal(false);
                    setConflictingName('');
                    // Retour au formulaire principal
                    onClose();
                }}
                existingName={conflictingName}
                onRetry={() => {
                    // Fermer la modale de conflit et garder le formulaire principal pour modification
                    setShowConflictModal(false);
                    setConflictingName('');
                    // Le nom reste dans le champ pour que l'utilisateur puisse le modifier
                }}
            />
        </>
    );
};

const JoinTenantModal = ({ onClose, onSuccess }) => {
    const [inviteCode, setInviteCode] = useState('');
    const [loading, setLoading] = useState(false);
    const { getAuthHeaders } = useAuth();
    const inviteInputRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/join/${inviteCode}`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                toast.success('Vous avez rejoint l\'organisation!');
                onSuccess();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Code invalide ou expiré');
            }
        } catch (error) {
            console.error('Error joining tenant:', error);
            toast.error('Erreur réseau');
        } finally {
            setLoading(false);
        }
    };

    const joinModalActions = (
        <>
            <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
            >
                Annuler
            </button>
            <button
                type="submit"
                form="join-tenant-form-inline"
                disabled={loading || inviteCode.length < 10}
                className="flex-1 bg-primary text-white dark:text-black font-bold py-2 px-4 rounded hover:bg-primary-hover disabled:bg-gray-400 dark:disabled:bg-gray-600"
            >
                {loading ? 'Vérification...' : 'Rejoindre'}
            </button>
        </>
    );

    return (
        <Modal
            open={true}
            onClose={onClose}
            title="Rejoindre une organisation"
            actions={joinModalActions}
            initialFocusRef={inviteInputRef}
        >
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Entrez le code d'invitation fourni par l'administrateur de l'organisation.
            </p>

            <form id="join-tenant-form-inline" onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Code d'invitation
                    </label>
                    <input
                        ref={inviteInputRef}
                        type="text"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                        className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary font-mono text-center text-lg tracking-wider"
                        required
                        disabled={loading}
                        placeholder="XXXX-XXXX-XXXX"
                        maxLength={14}
                    />
                </div>
            </form>
        </Modal>
    );
};

export default JoinTenant;